/**
 * AI Spend Approvals API
 *
 * Handles spend request listing and approval/denial operations.
 */

import { apiClient } from './client';

// Types
export interface SpendRequest {
  request_id: string;
  status: 'pending' | 'approved' | 'denied' | 'expired' | 'cancelled';
  quote_id: string;
  estimated_cost: number | null;
  justification: string | null;
  decision_reason: string | null;
  decided_by: string | null;
  decided_at: string | null;
  expires_at: string;
  created_at: string;
  // Extended info (populated from joins on backend, may not always be available)
  requester_email?: string;
  requester_name?: string;
  matter_name?: string;
  matter_id?: string;
  operation_type?: string;
}

export interface ApprovalRequest {
  reason?: string;
}

export interface ListSpendRequestsParams {
  status?: 'pending' | 'approved' | 'denied' | 'expired' | 'cancelled' | 'all';
}

// API Functions
export const approvalsApi = {
  /**
   * List spend requests with optional status filter
   */
  async list(params?: ListSpendRequestsParams): Promise<SpendRequest[]> {
    const queryParams: Record<string, string> = {};
    if (params?.status && params.status !== 'all') {
      queryParams.status = params.status;
    }

    const response = await apiClient.get<SpendRequest[]>('/api/ai/spend-requests', {
      params: queryParams,
    });
    return response.data;
  },

  /**
   * Approve a spend request
   */
  async approve(requestId: string, reason?: string): Promise<SpendRequest> {
    const body: ApprovalRequest = reason ? { reason } : {};
    const response = await apiClient.post<SpendRequest>(
      `/api/ai/spend-requests/${requestId}/approve`,
      body
    );
    return response.data;
  },

  /**
   * Deny a spend request (reason required)
   */
  async deny(requestId: string, reason: string): Promise<SpendRequest> {
    const response = await apiClient.post<SpendRequest>(
      `/api/ai/spend-requests/${requestId}/deny`,
      { reason }
    );
    return response.data;
  },
};

export default approvalsApi;
